// Step order and specialty ordering for the find-a-teacher questionnaire.
//
// Pulled out of the component so both rules can be tested on their own: the
// order the visitor sees decides what they can pick, and the step list decides
// which question each index renders.

// Level and goals are questions about learning a language: how fluent are you,
// and what do you want the language for. A maths student answers neither —
// their stage and their purpose are both in the specialty list already, as
// "國中數學" or "考試準備". They share one screen, as do the schedule and the
// optional note: four screens lose fewer visitors than six.
const LANGUAGE_ONLY_STEPS = ['level_goals']

// Which answer fields each screen holds.
const SCREEN_FIELDS = {
  subject: ['subject'],
  level_goals: ['level', 'goals'],
  tags: ['tags'],
  schedule_note: ['days', 'periods', 'situation']
}

// The specialty list keeps every tag the subject offers. The goals the visitor
// picked only pull the matching ones to the front, because a visitor can pick
// the wrong goal, or skip the goal step, and still know the tag they want.
export function orderTagsByGoals(options, goals, goalTagOrder) {
  if (!goals || !goals.length) return options

  const preferred = new Set(goals.flatMap((goal) => goalTagOrder[goal] || []))
  const hit = options.filter((option) => preferred.has(option.value))
  const rest = options.filter((option) => !preferred.has(option.value))

  return [...hit, ...rest]
}

// A subject whose tags are all level tags has nothing left to ask, so the
// specialty step disappears rather than rendering an empty question. Steps are
// addressed by name for exactly this reason: a fixed index would put the
// schedule question under the wrong number for those subjects.
//
// @param {number} tagOptionCount
// @param {boolean} asksLevelAndGoals - false for a subject that is not a language
export function stepIdsFor(tagOptionCount, asksLevelAndGoals = true) {
  const steps = ['subject', 'level_goals', 'tags', 'schedule_note']

  return steps.filter((step) => {
    if (step === 'tags') return Boolean(tagOptionCount)
    if (LANGUAGE_ONLY_STEPS.includes(step)) return asksLevelAndGoals
    return true
  })
}

// Which questions a prefilled link leaves to ask.
//
// A fact the linking page states in its own address — the subject, the goal,
// the specialty — is removed from the flow: asking it again is asking the
// visitor to repeat the address they arrived on.
//
// An inference is not removed. The level is converted from an IELTS band by an
// estimate, and the level field accepts no answer at all; a visitor who never
// sees the guess cannot correct it, and a wrong level reaches the matcher with
// nothing on screen saying it was inferred.
//
// `skipped` is returned so the first shown step can offer them back. A visitor
// who arrived from an IELTS page may want TOEIC, and a removed step has no
// other way in.
const PREFILL_FACTS = ['subject', 'goals', 'tags']

function isPrefilled(prefill, key) {
  const value = prefill?.[key]

  return Array.isArray(value) ? value.length > 0 : Boolean(value)
}

export function flowForPrefill(prefill, tagOptionCount, asksLevelAndGoals = true) {
  const available = stepIdsFor(tagOptionCount, asksLevelAndGoals)
  const askedFields = available.flatMap((step) => SCREEN_FIELDS[step])
  const skipped = PREFILL_FACTS.filter((field) => askedFields.includes(field) && isPrefilled(prefill, field))
  // A screen goes only when every field on it was a prefilled fact. The goal
  // shares its screen with the level, an inference, so that screen stays and
  // only the goal question on it is hidden.
  const stepIds = available.filter((step) => !SCREEN_FIELDS[step].every((field) => skipped.includes(field)))
  const hiddenFields = skipped.filter((field) => stepIds.some((step) => SCREEN_FIELDS[step].includes(field)))

  return { stepIds, skipped, hiddenFields, startStep: stepIds[0], allStepIds: available }
}

function answered(answers, field) {
  const value = answers?.[field]

  return Array.isArray(value) ? value.length > 0 : Boolean(value)
}

// Where a resumed draft opens: the first screen with an unanswered field.
// ⛔ Not the stored index. Drafts saved under the six-step layout point at a
// different screen now, and the answers say where the visitor stopped.
//
// @param {Object} answers
// @param {string[]} stepIds the screens this flow has
// @returns {number} an index into stepIds
export function resumeStepFor(answers, stepIds) {
  const index = stepIds.findIndex((step) => {
    if (step === 'level_goals') return !answered(answers, 'level') && !answered(answers, 'goals')
    if (step === 'schedule_note') return !answered(answers, 'days') || !answered(answers, 'periods')

    return SCREEN_FIELDS[step].some((field) => !answered(answers, field))
  })

  return index === -1 ? stepIds.length - 1 : index
}
